import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, MapPin, Clock, Loader2, Trash2, AlertTriangle, CalendarClock, Phone, MessageSquare, Mail, Star } from 'lucide-react';
import { Input } from './ui/input';
import { API, useAuth } from '../App';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const spamReasons = (r) => {
  const reasons = [];
  if (r.source === 'manual') return reasons;
  const text = `${r.contact_name} ${r.company_name || ''} ${r.notes || ''} ${r.address}`;
  if (/https?:\/\/|www\.|\.ru\b|\.xyz\b/i.test(text)) reasons.push('contains links');
  if (/[\u0400-\u04FF\u4E00-\u9FFF]/.test(text)) reasons.push('foreign characters');
  const name = (r.contact_name || '').trim();
  if (!name.includes(' ') && (name.match(/[A-Z]/g) || []).length >= 3) reasons.push('random-looking name');
  if ((r.contact_phone || '').replace(/\D/g, '').length < 10) reasons.push('invalid phone');
  if ((r.address || '').trim().length < 6) reasons.push('no real address');
  return reasons;
};

const formatReceived = (iso) => {
  if (!iso) return 'Unknown';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const smsHref = (phone, body) => `sms:${(phone || '').replace(/[^\d+]/g, '')}?&body=${encodeURIComponent(body)}`;
const reviewText = (name, link) => `Hi ${(name || '').split(' ')[0]}! Thank you for having Espresso Beso at your event. If you enjoyed our coffee, would you leave us a quick Google review? It really helps us: ${link}`;

function ContactButtons({ request, reviewLink }) {
  const btn = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors';
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid={`contact-buttons-${request.id}`}>
      <a href={`tel:${request.contact_phone}`} className={`${btn} border-green-600 text-green-700 hover:bg-green-50`} data-testid={`call-request-${request.id}`}><Phone className="w-3.5 h-3.5" />Call</a>
      <a href={smsHref(request.contact_phone, `Hi ${(request.contact_name || '').split(' ')[0]}, this is Espresso Beso about your event request. `)} className={`${btn} border-blue-600 text-blue-700 hover:bg-blue-50`} data-testid={`text-request-${request.id}`}><MessageSquare className="w-3.5 h-3.5" />Text</a>
      {request.contact_email && <a href={`mailto:${request.contact_email}?subject=${encodeURIComponent('Your Espresso Beso event request')}`} className={`${btn} border-espresso/40 text-espresso hover:bg-cream`} data-testid={`email-request-${request.id}`}><Mail className="w-3.5 h-3.5" />Email</a>}
      {request.status === 'completed' && (
        <a href={smsHref(request.contact_phone, reviewText(request.contact_name, reviewLink))} className={`${btn} border-gold bg-gold/10 text-espresso hover:bg-gold/20`} data-testid={`review-sms-${request.id}`}><Star className="w-3.5 h-3.5 text-gold" />Text review link</a>
      )}
      {request.review_email_sent_at && (
        <span className="text-xs text-green-700 self-center" data-testid={`review-email-sent-${request.id}`}>Review email sent {new Date(request.review_email_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      )}
    </div>
  );
}

function ReviewLinkSetting({ value, onSave }) {
  const [link, setLink] = useState(value);
  useEffect(() => setLink(value), [value]);
  return (
    <div className="admin-card" data-testid="review-link-setting">
      <p className="font-semibold text-espresso flex items-center gap-2 mb-1"><Star className="w-4 h-4 text-gold" />Google review link</p>
      <p className="text-xs text-espresso/60 mb-3">Sent by email automatically when you mark an event Completed, and used by the "Text review link" button.</p>
      <div className="flex gap-2">
        <Input value={link} onChange={(e) => setLink(e.target.value)} data-testid="review-link-input" />
        <Button onClick={() => onSave(link)} disabled={link === value} data-testid="review-link-save">Save</Button>
      </div>
    </div>
  );
}

function RequestCard({ request, selected, onToggle, onStatus, onDelete, reviewLink }) {
  const reasons = spamReasons(request);
  return (
    <div className={`admin-card ${reasons.length ? 'border-l-4 border-l-red-400' : ''}`} data-testid={`group-request-${request.id}`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
        <div className="flex items-start gap-3">
          <Checkbox checked={selected} onCheckedChange={onToggle} className="mt-1" data-testid={`select-request-${request.id}`} />
          <div>
            <h3 className="font-semibold text-espresso">{request.contact_name}</h3>
            <p className="text-sm text-espresso/60">{request.company_name || 'Individual'} · {request.event_type} · {request.package_size}{request.source === 'manual' ? ' · Added by you' : ''}</p>
            <p className="text-xs text-espresso/50 flex items-center gap-1 mt-1" data-testid={`request-received-${request.id}`}>
              <CalendarClock className="w-3 h-3" /> Received {formatReceived(request.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pl-7 sm:pl-0">
          <Select value={request.status} onValueChange={onStatus}>
            <SelectTrigger className="w-32" data-testid={`request-status-${request.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={onDelete} className="text-red-600 hover:bg-red-50" data-testid={`delete-request-${request.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {reasons.length > 0 && (
        <p className="text-xs text-red-700 bg-red-50 rounded-md px-3 py-1.5 mb-3 flex items-center gap-1.5" data-testid={`spam-badge-${request.id}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Possible spam: {reasons.join(', ')}
        </p>
      )}
      <div className="space-y-2 text-sm">
        <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" /><span className="text-espresso/70">{request.address}</span></p>
        <p className="flex items-center gap-2"><Users className="w-4 h-4 text-gold" /><span className="text-espresso/70">{request.people_count} people</span></p>
        <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-gold" /><span className="text-espresso/70">Event: {request.preferred_date} at {request.preferred_time}</span></p>
      </div>
      <div className="mt-4 pt-4 border-t border-cream-dark text-sm break-words">
        <p><span className="text-espresso/60">Email:</span> {request.contact_email}</p>
        <p><span className="text-espresso/60">Phone:</span> {request.contact_phone}</p>
        {request.notes && <p className="mt-2"><span className="text-espresso/60">Notes:</span> {request.notes}</p>}
        <ContactButtons request={request} reviewLink={reviewLink} />
      </div>
    </div>
  );
}

export default function GroupRequestsManager() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [reviewLink, setReviewLink] = useState('');

  useEffect(() => {
    axios.get(`${API}/business-settings`, { headers }).then((res) => setReviewLink(res.data.review_link)).catch(() => {});
  }, [token]);

  const saveReviewLink = async (link) => {
    try {
      const res = await axios.put(`${API}/business-settings`, { review_link: link }, { headers });
      setReviewLink(res.data.review_link);
      toast.success('Review link saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save link');
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/group-requests`, { headers });
      setRequests(res.data);
    } catch (e) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [token]);

  const spamIds = requests.filter((r) => spamReasons(r).length).map((r) => r.id);
  const visible = filter === 'spam' ? requests.filter((r) => spamIds.includes(r.id)) : requests;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const deleteIds = async (ids) => {
    if (!ids.length || !window.confirm(`Delete ${ids.length} request${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/group-requests/bulk-delete`, { ids }, { headers });
      toast.success(`Deleted ${res.data.deleted} request${res.data.deleted === 1 ? '' : 's'}`);
      setSelected(new Set());
      fetchRequests();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(`${API}/group-requests/${id}/status`, { status }, { headers });
      toast.success(res.data.review_email_sent ? 'Marked completed – Google review request emailed to client' : 'Request status updated');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }

  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  return (
    <div className="space-y-6" data-testid="admin-group-requests">
      <h1 className="section-title">Group Requests</h1>
      <div className="admin-card flex flex-wrap items-center gap-3" data-testid="requests-toolbar">
        <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => { setFilter('all'); setSelected(new Set()); }} data-testid="filter-all">All ({requests.length})</Button>
        <Button variant={filter === 'spam' ? 'default' : 'outline'} size="sm" onClick={() => { setFilter('spam'); setSelected(new Set()); }} data-testid="filter-spam">Possible spam ({spamIds.length})</Button>
        <label className="flex items-center gap-2 text-sm text-espresso ml-auto">
          <Checkbox checked={allVisibleSelected} onCheckedChange={(c) => setSelected(c ? new Set(visible.map((r) => r.id)) : new Set())} data-testid="select-all-requests" />
          Select all
        </label>
        <Button size="sm" variant="destructive" disabled={!selected.size} onClick={() => deleteIds([...selected])} className="gap-1.5" data-testid="delete-selected-requests">
          <Trash2 className="w-4 h-4" /> Delete selected ({selected.size})
        </Button>
      </div>
      {visible.length === 0 ? (
        <div className="admin-card text-center py-12">
          <Users className="w-12 h-12 text-espresso/30 mx-auto mb-4" />
          <p className="text-espresso/60">{filter === 'spam' ? 'No possible spam found' : 'No group requests yet'}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {visible.map((r) => (
            <RequestCard key={r.id} request={r} selected={selected.has(r.id)} onToggle={() => toggle(r.id)} onStatus={(v) => updateStatus(r.id, v)} onDelete={() => deleteIds([r.id])} reviewLink={reviewLink} />
          ))}
        </div>
      )}
      <ReviewLinkSetting value={reviewLink} onSave={saveReviewLink} />
    </div>
  );
}
